<script>
  // Mobile shell: on phones there is no window chrome, so we show the launcher
  // grid and, when an app is open, render its component full-screen on top with
  // a "back to apps" bar. Without this surface, tapping an app in the launcher
  // added a window to state that nothing ever rendered — so apps appeared to
  // never open on mobile (desktop renders windows via Desktop.svelte).
  import MobileLauncher from './MobileLauncher.svelte';
  import ErrorBoundary from '../../apps/ErrorBoundary.svelte';
  import { windows, minimizeWindow, closeWindow } from '../../os/windows.svelte.js';

  // The foreground app is the top-most non-minimized window. One app fills the
  // screen at a time; tapping another app in the launcher brings it forward.
  const foreground = $derived(
    windows.filter((w) => !w.minimized).sort((a, b) => b.z - a.z)[0] ?? null
  );

  // "Apps" acts like a phone home button: minimise every open window so the
  // launcher (rendered underneath) shows. Apps stay in state, so tapping one
  // again brings it back with its content intact.
  function back() {
    for (const win of windows) {
      if (!win.minimized) minimizeWindow(win.id);
    }
  }

  function close() {
    if (foreground) closeWindow(foreground.id);
  }
</script>

<MobileLauncher />

{#if foreground}
  <div class="mobile-app" role="dialog" aria-label={foreground.title}>
    <header class="ma-bar">
      <button class="ma-back" onclick={back} aria-label="Back to apps">‹ Apps</button>
      <span class="ma-title">{foreground.title}</span>
      <button class="ma-close" onclick={close} aria-label="Close app">✕</button>
    </header>
    <div class="ma-body">
      {#if foreground.component}
        {@const Comp = foreground.component}
        <ErrorBoundary name={foreground.title ?? foreground.id}>
          <Comp windowId={foreground.id} {...foreground.props} />
        </ErrorBoundary>
      {/if}
    </div>
  </div>
{/if}

<style>
  .mobile-app {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    background: var(--window-bg, #262626);
    color: var(--text, #e8e8e8);
  }
  .ma-bar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 44px;
    padding: 0 8px;
    background: #25292a;
    border-bottom: 1px solid #0d0f10;
    color: #e6e8e9;
  }
  .ma-back {
    background: none;
    border: none;
    color: #4d91c9;
    font-size: 16px;
    padding: 8px 6px;
    cursor: pointer;
    touch-action: manipulation;
  }
  .ma-title {
    flex: 1;
    min-width: 0;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ma-close {
    background: none;
    border: none;
    color: #c9cdce;
    font-size: 15px;
    padding: 8px 10px;
    cursor: pointer;
    touch-action: manipulation;
  }
  .ma-body {
    flex: 1;
    position: relative;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
</style>
